import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Address, AddressQuery, AddressService, AddressType, ErrorStateService, MemberDetails, MemberQuery } from '@fgb/core';

@Component({
  selector: 'fgb-edit-shipping-address',
  templateUrl: './edit-shipping-address.component.html',
  styleUrl: './edit-shipping-address.component.scss',
})
export class EditShippingAddressComponent implements OnInit, OnDestroy {
  shippingAddress: Address;
  addresses: Address[];
  shippingAddressForm: FormGroup;
  memberDetails: MemberDetails;
  editClicked = false;
  baseData: any;

  @Output() saved = new EventEmitter();
  @Output() editing = new EventEmitter<boolean>();

  constructor(
    private addressQuery: AddressQuery,
    private memberQuery: MemberQuery,
    private formBuilder: FormBuilder,
    private addressService: AddressService,
    private errorService: ErrorStateService
  ) {}

  ngOnInit() {
    this.errorService.clearErrors();
    this.addressQuery
      .selectAddresses([AddressType.DefaultAddress])
      .subscribe((addresses) => {
        this.addresses = addresses;
        this.memberQuery
          .selectMemberDetails()
          .subscribe((md) => {
            // get member details
            if (md) {
              this.memberDetails = md;
            }

            // get shipping address details
            this.shippingAddress = this._getDefaultShippingAddress();
          })
          .unsubscribe();
      })
      .unsubscribe();

    this.shippingAddressForm = this.formBuilder.group({
      Street: [
        this.shippingAddress.Street,
        [Validators.required, this.noWhitespaceValidator, Validators.pattern('^([a-zA-Z0-9 ,.])+$')],
      ],
      Town: [
        this.shippingAddress.Town,
        [Validators.required, this.noWhitespaceValidator, Validators.pattern('^([a-zA-Z0-9 ])+$')],
      ],
      County: [
        this.shippingAddress.County,
        [Validators.required, this.noWhitespaceValidator, Validators.pattern('^([a-zA-Z0-9 ])+$')],
      ],
      Postcode: [
        this.shippingAddress.Postcode,
        [Validators.required, this.noWhitespaceValidator, Validators.pattern('^([a-zA-Z0-9 ])+$')],
      ],
      Country: [
        this.shippingAddress.Country,
        [Validators.required, this.noWhitespaceValidator, Validators.pattern('^([a-zA-Z0-9 ])+$')],
      ],
      PortalId: [this.memberDetails.PortalId],
      ClubId: [this.memberDetails.ClubId],
      AddressType: AddressType.ShippingAddress1,
    });

    this.baseData = { ...this.shippingAddressForm.value };
  }

  public noWhitespaceValidator(control: FormControl) {
    if (control.value === '') return;
    return control.value?.trim().length ? null : { whitespace: true };
  }

  // get shipping address details
  private _getDefaultShippingAddress(): Address {
    if (this.addresses) {
      let shippingAddress1 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress1).pop() as Address;
      if (shippingAddress1) {
        return shippingAddress1;
      }

      let shippingAddress2 = this.addresses.filter((a) => a.AddressType === AddressType.ShippingAddress2).pop() as Address;
      if (shippingAddress2) {
        return shippingAddress2;
      }
    }

    return {
      AddressName: 'Shipping Address',
      FirstName: this.memberDetails.FirstName,
      Surname: this.memberDetails.Surname,
      Street: this.memberDetails.Street,
      Town: this.memberDetails.Town,
      County: this.memberDetails.County,
      Postcode: this.memberDetails.PostCode,
      Country: this.memberDetails.Country,
      HomeNumber: this.memberDetails.HomeNumber,
      EmailAddress: this.memberDetails.EmailAddress1,
      PortalId: this.memberDetails.PortalId,
      ClubId: this.memberDetails.ClubId,
      AddressType: AddressType.ShippingAddress1,
    } as Address;
  }

  disableIfFormIncomplete(): string {
    const formComplete: Boolean =
      this.shippingAddressForm.controls.Street.value !== '' &&
      this.shippingAddressForm.controls.Street.value !== null &&
      this.shippingAddressForm.controls.Town.value !== '' &&
      this.shippingAddressForm.controls.Town.value !== null &&
      this.shippingAddressForm.controls.County.value !== '' &&
      this.shippingAddressForm.controls.County.value !== null &&
      this.shippingAddressForm.controls.Country.value !== '' &&
      this.shippingAddressForm.controls.Country.value !== null &&
      this.shippingAddressForm.controls.PostCode?.value !== '';
    this.shippingAddressForm.controls.PostCode?.value !== null;
    const formValid: Boolean = this.shippingAddressForm.valid;
    if (formComplete && formValid) {
      return '';
    } else {
      return 'disabled';
    }
  }

  // edit details click
  editShippingDetails() {
    this.editClicked = true;
    this.editing.emit(true);
  }

  // cancel edit details
  cancelEdit() {
    this.errorService.clearErrors();
    this.editClicked = false;
    this.editing.emit(false);
    this.shippingAddressForm.patchValue(this.baseData);
  }

  get f() {
    return this.shippingAddressForm.controls;
  }

  // save edit details
  saveChanges() {
    this.shippingAddressForm.patchValue({
      ...this.shippingAddressForm.value,
      Street: this.shippingAddressForm.value.Street.trim(),
      Country: this.shippingAddressForm.value.Country.trim(),
      County: this.shippingAddressForm.value.County.trim(),
      Town: this.shippingAddressForm.value.Town.trim(),
      Postcode: this.shippingAddressForm.value.Postcode.trim(),
    });

    this.errorService.clearErrors();
    if (this.shippingAddressForm.valid) {
      this.shippingAddress = { ...this.shippingAddress, ...this.shippingAddressForm.value };
      this.editClicked = false;
      this.addressService.saveAddress(this.shippingAddress);
      this.saved.emit(this.shippingAddress);
      this.shippingAddressForm.patchValue(this.shippingAddress);
    }
  }

  ngOnDestroy(): void {
    this.editing.emit(false);
  }
}
